import * as Y from 'yjs';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { api, ApiError, base64ToBytes, bytesToBase64, NetworkError } from './api';
import type { Role } from './boardsApi';

export type SyncStatus = 'connecting' | 'synced' | 'syncing' | 'offline' | 'error';

export interface SyncState {
  status: SyncStatus;
  /** Human-readable reason when status is 'error' (403, 404, ...). */
  error: string | null;
  role: Role | null;
  lastSeq: number;
  queued: number;
}

interface DocResponse {
  snapshot: string | null;
  snapshotSeq: number;
  updates: { seq: number; clientId: string; data: string }[];
  seq: number;
  role: Role;
}

interface SinceResponse {
  reset: boolean;
  snapshot?: string;
  updates: { seq: number; clientId: string; data: string }[];
  seq: number;
  pending: number;
  /** A full page was read: ask again at once. Missing from older servers. */
  more?: boolean;
}

/**
 * How eagerly a board syncs, chosen per board in Board settings (`meta.sync`; unset is
 * 'balanced'). Polling only stays fast while changes from elsewhere keep arriving: every poll
 * that brings nothing stretches the wait by POLL_GROWTH, up to `pollMax`. Local edits are sent
 * once they pause for `pushDelay`, and at least every `pushMaxWait` while they keep coming.
 */
export type SyncMode = 'live' | 'balanced' | 'saver';

export interface SyncProfile {
  pollMin: number;
  pollMax: number;
  pushDelay: number;
  pushMaxWait: number;
}

export const SYNC_PROFILES: Record<SyncMode, SyncProfile> = {
  live: { pollMin: 2000, pollMax: 5000, pushDelay: 300, pushMaxWait: 1000 },
  balanced: { pollMin: 2000, pollMax: 30000, pushDelay: 1000, pushMaxWait: 4000 },
  saver: { pollMin: 5000, pollMax: 60000, pushDelay: 2500, pushMaxWait: 10000 },
};

export const isSyncMode = (v: unknown): v is SyncMode => v === 'live' || v === 'balanced' || v === 'saver';

const POLL_GROWTH = 1.5;
const PAGE = 500;
const COMPACT_AFTER = 300;
/** Focus and visibility both fire when coming back to a tab; one poll is enough. */
const CATCH_UP_GAP_MS = 1500;
/** Browsers refuse keepalive bodies over 64 KB; bigger leftovers wait for the next connect. */
const KEEPALIVE_MAX_CHARS = 60000;

/**
 * Yjs "provider" over plain HTTP against the PHP update log (docs/02-architecture.md section 3).
 *
 * - connect(): fetch snapshot + updates, apply them, then send whatever the local doc has that
 *   the server lacks (the offline diff). No separate pending queue needs to survive reloads.
 * - Local updates are batched and POSTed (see SyncProfile). Polls name our clientId so the
 *   server leaves our own updates out of the answer.
 * - Polling backs off while nothing arrives from elsewhere, pauses while the tab is hidden, and
 *   catches up at once when the tab or window comes back or the network returns.
 * - Leftover edits are sent when the tab is hidden, the page goes away, or the document closes.
 * - When the server log grows past COMPACT_AFTER entries, a synced editor uploads a snapshot.
 */
export class HttpProvider {
  readonly store: StoreApi<SyncState>;
  private readonly clientId: string;
  private readonly meta: Y.Map<unknown>;
  private defaultMode: SyncMode = 'balanced';
  private queue: Uint8Array[] = [];
  /** When the oldest unsent update was queued (0: nothing waiting). */
  private firstQueuedAt = 0;
  private inFlight = false;
  private polling = false;
  private lastPollAt = 0;
  private pollInterval = SYNC_PROFILES.balanced.pollMin;
  private flushTimer: ReturnType<typeof setTimeout> | undefined;
  private pollTimer: ReturnType<typeof setTimeout> | undefined;
  private retryDelay = 2000;
  private destroyed = false;
  private connected = false;

  constructor(
    private readonly doc: Y.Doc,
    private readonly boardId: string,
  ) {
    this.clientId = String(doc.clientID);
    this.meta = doc.getMap('meta');
    this.store = createStore<SyncState>(() => ({
      status: 'connecting',
      error: null,
      role: null,
      lastSeq: 0,
      queued: 0,
    }));
    doc.on('update', this.onLocalUpdate);
    this.meta.observe(this.onMeta);
    document.addEventListener('visibilitychange', this.onVisibility);
    window.addEventListener('focus', this.onFocus);
    window.addEventListener('pagehide', this.onPageHide);
    window.addEventListener('online', this.onOnline);
  }

  get canEdit(): boolean {
    const role = this.store.getState().role;
    return role === 'owner' || role === 'editor';
  }

  /** The board's own choice, else the default (the cutout drawer defaults to 'saver'). */
  get mode(): SyncMode {
    const m = this.meta.get('sync');
    return isSyncMode(m) ? m : this.defaultMode;
  }

  setDefaultMode(mode: SyncMode): void {
    if (mode === this.defaultMode) return;
    this.defaultMode = mode;
    this.modeChanged();
  }

  private get profile(): SyncProfile {
    return SYNC_PROFILES[this.mode];
  }

  async connect(): Promise<void> {
    if (this.destroyed) return;
    let res: DocResponse;
    try {
      res = await api.get<DocResponse>(`/boards/${this.boardId}/doc`);
    } catch (e) {
      this.handleFailure(e, 'connect');
      return;
    }
    if (this.destroyed) return;

    // Server state as its own doc so we can compute a state vector, then merge into ours.
    const server = new Y.Doc();
    let serverVector: Uint8Array;
    try {
      if (res.snapshot) Y.applyUpdate(server, base64ToBytes(res.snapshot));
      for (const u of res.updates) Y.applyUpdate(server, base64ToBytes(u.data));
      serverVector = Y.encodeStateVector(server);
      Y.applyUpdate(this.doc, Y.encodeStateAsUpdate(server), this);
    } catch (e) {
      console.error('[sync] server document is unreadable', e);
      this.store.setState({ status: 'error', error: 'Server data unreadable' });
      return;
    } finally {
      server.destroy();
    }

    // Whatever we have that the server does not: edits made offline or before this board was synced.
    const diff = Y.encodeStateAsUpdate(this.doc, serverVector);
    if (diff.byteLength > 2 && (res.role === 'owner' || res.role === 'editor')) {
      this.queue.unshift(diff);
    }

    this.connected = true;
    this.store.setState({ role: res.role, lastSeq: res.seq, error: null, queued: this.queue.length });
    this.reachedServer();
    void this.flush();
    this.pollInterval = this.profile.pollMin;
    this.schedulePoll(this.pollInterval);
  }

  private onLocalUpdate = (update: Uint8Array, origin: unknown): void => {
    if (origin === this || this.destroyed) return;
    if (!this.canEdit && this.connected) return; // viewer: keep local, never send
    this.queue.push(update);
    if (!this.firstQueuedAt) this.firstQueuedAt = Date.now();
    this.store.setState({ queued: this.queue.length });
    this.updateStatus();
    this.scheduleFlush();
  };

  /** Send once local edits pause, but never hold the oldest one longer than the profile allows. */
  private scheduleFlush(): void {
    if (this.destroyed || !this.queue.length) return;
    // while offline the retry timer is in charge; don't hammer the server on every edit
    if (this.store.getState().status === 'offline') return;
    const { pushDelay, pushMaxWait } = this.profile;
    const first = this.firstQueuedAt || Date.now();
    const wait = Math.max(0, Math.min(pushDelay, first + pushMaxWait - Date.now()));
    clearTimeout(this.flushTimer);
    this.flushTimer = setTimeout(() => void this.flush(), wait);
  }

  private async flush(): Promise<void> {
    if (this.destroyed || !this.connected || this.inFlight || !this.queue.length || !this.canEdit) return;
    clearTimeout(this.flushTimer);
    const batch = this.queue.splice(0);
    this.firstQueuedAt = 0;
    const merged = batch.length === 1 ? batch[0]! : Y.mergeUpdates(batch);
    this.inFlight = true;
    this.updateStatus();
    let ok = false;
    try {
      await api.post(`/boards/${this.boardId}/updates`, {
        clientId: this.clientId,
        updates: [bytesToBase64(merged)],
      });
      ok = true;
    } catch (e) {
      this.queue.unshift(merged);
      if (!this.firstQueuedAt) this.firstQueuedAt = Date.now();
      this.handleFailure(e, 'flush');
    } finally {
      this.inFlight = false;
      this.store.setState({ queued: this.queue.length });
      this.updateStatus();
    }
    if (ok) {
      this.reachedServer();
      // edits made while this one was on its way keep their own timing
      this.scheduleFlush();
    }
  }

  /**
   * Sends everything still queued in one request, outside the normal timing: the page is going
   * away or the document is closing. keepalive lets the request outlive the page.
   */
  private sendRemaining(): void {
    if (!this.queue.length || !this.connected || !this.canEdit) return;
    clearTimeout(this.flushTimer);
    const batch = this.queue.splice(0);
    this.firstQueuedAt = 0;
    const merged = batch.length === 1 ? batch[0]! : Y.mergeUpdates(batch);
    const data = bytesToBase64(merged);
    api
      .post(`/boards/${this.boardId}/updates`, { clientId: this.clientId, updates: [data] }, { keepalive: data.length < KEEPALIVE_MAX_CHARS })
      .catch(() => {
        // still alive (a page restored from the back/forward cache): try again the normal way;
        // a closed document's edits stay in IndexedDB and go up as the diff on its next connect
        if (this.destroyed) return;
        this.queue.unshift(merged);
        if (!this.firstQueuedAt) this.firstQueuedAt = Date.now();
        this.scheduleFlush();
      });
    this.store.setState({ queued: this.queue.length });
  }

  private async poll(): Promise<void> {
    if (this.destroyed || !this.connected || document.hidden || this.polling) return;
    clearTimeout(this.pollTimer);
    this.polling = true;
    this.lastPollAt = Date.now();
    let res: SinceResponse;
    try {
      res = await api.get<SinceResponse>(
        `/boards/${this.boardId}/updates?since=${this.store.getState().lastSeq}&client=${encodeURIComponent(this.clientId)}`,
      );
    } catch (e) {
      this.polling = false;
      this.handleFailure(e, 'poll');
      return;
    }
    this.polling = false;
    if (this.destroyed) return;

    let foreign = 0;
    try {
      this.doc.transact(() => {
        if (res.reset && res.snapshot) Y.applyUpdate(this.doc, base64ToBytes(res.snapshot), this);
        for (const u of res.updates) {
          if (u.clientId === this.clientId) continue; // an older server still echoes ours
          Y.applyUpdate(this.doc, base64ToBytes(u.data), this);
          foreign++;
        }
      }, this);
    } catch (e) {
      console.error('[sync] bad update from server', e);
      this.store.setState({ status: 'error', error: 'Server data unreadable' });
      return;
    }

    this.store.setState({ lastSeq: Math.max(this.store.getState().lastSeq, res.seq), error: null });
    this.reachedServer();

    if (res.more ?? res.updates.length >= PAGE) {
      this.schedulePoll(0);
      return;
    }
    if (res.pending > COMPACT_AFTER && !this.queue.length && !this.inFlight && this.canEdit && Math.random() < 0.3) {
      void this.compact();
    }
    // someone else is at work: stay quick; otherwise ease off
    const { pollMin, pollMax } = this.profile;
    this.pollInterval = foreign ? pollMin : Math.min(pollMax, Math.max(pollMin, this.pollInterval * POLL_GROWTH));
    this.schedulePoll(this.pollInterval);
  }

  private async compact(): Promise<void> {
    const seq = this.store.getState().lastSeq;
    try {
      await api.post(`/boards/${this.boardId}/snapshot`, {
        seq,
        data: bytesToBase64(Y.encodeStateAsUpdate(this.doc)),
      });
    } catch (e) {
      console.warn('[sync] snapshot failed', e);
    }
  }

  private schedulePoll(ms: number): void {
    clearTimeout(this.pollTimer);
    if (this.destroyed) return;
    this.pollTimer = setTimeout(() => void this.poll(), ms);
  }

  /** Poll now, unless one just ran or is running. */
  private catchUp(): void {
    if (!this.connected || document.hidden || this.store.getState().status === 'error') return;
    if (this.polling || Date.now() - this.lastPollAt < CATCH_UP_GAP_MS) return;
    this.schedulePoll(0);
  }

  private handleFailure(e: unknown, retry: 'connect' | 'poll' | 'flush'): void {
    if (this.destroyed) return;
    if (e instanceof ApiError && e.status !== 429 && e.status < 500) {
      // 401/403/404: nothing to retry until the user does something
      this.store.setState({ status: 'error', error: e.message });
      return;
    }
    const wasOffline = this.store.getState().status === 'offline';
    this.store.setState({ status: 'offline', error: e instanceof NetworkError ? null : String(e) });
    if (!wasOffline) console.warn('[sync] offline:', e);
    this.retryDelay = Math.min(this.retryDelay * 2, 60000);
    if (retry === 'flush') {
      clearTimeout(this.flushTimer);
      this.flushTimer = setTimeout(() => void this.flush(), this.retryDelay);
    } else {
      clearTimeout(this.pollTimer);
      this.pollTimer = setTimeout(() => void (retry === 'poll' ? this.poll() : this.connect()), this.retryDelay);
    }
  }

  /** A request went through: leave the offline state and send what waited meanwhile. */
  private reachedServer(): void {
    this.retryDelay = 2000;
    if (this.store.getState().status === 'offline') {
      this.store.setState({ status: 'syncing', error: null });
      this.scheduleFlush();
    }
    this.updateStatus();
  }

  private updateStatus(): void {
    const s = this.store.getState();
    if (s.status === 'error' || s.status === 'offline') return;
    const busy = this.queue.length > 0 || this.inFlight;
    const status: SyncStatus = !this.connected ? 'connecting' : busy ? 'syncing' : 'synced';
    if (status !== s.status) this.store.setState({ status });
  }

  /** A new mode applies at once: its timings for what is queued, and a fresh, quick poll. */
  private modeChanged(): void {
    if (!this.connected) return;
    this.scheduleFlush();
    this.pollInterval = this.profile.pollMin;
    if (!this.polling) this.schedulePoll(this.pollInterval);
  }

  private onMeta = (e: Y.YMapEvent<unknown>): void => {
    if (e.keysChanged.has('sync')) this.modeChanged();
  };

  private onVisibility = (): void => {
    if (document.hidden) {
      // the page stays alive in a background tab, so the ordinary request can finish
      if (this.queue.length) void this.flush();
    } else this.catchUp();
  };

  private onFocus = (): void => this.catchUp();

  private onPageHide = (): void => this.sendRemaining();

  private onOnline = (): void => {
    this.retryDelay = 2000;
    if (this.connected) {
      this.store.setState({ status: 'syncing', error: null });
      this.updateStatus();
      void this.flush();
      this.schedulePoll(0);
    } else {
      void this.connect();
    }
  };

  destroy(): void {
    this.sendRemaining();
    this.destroyed = true;
    clearTimeout(this.flushTimer);
    clearTimeout(this.pollTimer);
    this.doc.off('update', this.onLocalUpdate);
    this.meta.unobserve(this.onMeta);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('focus', this.onFocus);
    window.removeEventListener('pagehide', this.onPageHide);
    window.removeEventListener('online', this.onOnline);
  }
}
